import {
  Box,
  InlineStack,
  Text,
  TextField,
  Button,
  Thumbnail,
  Badge,
  Icon,
} from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import { t } from '../i18n';

const PLACEHOLDER =
  'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

export interface SelectedProductLike {
  id: string;
  title: string;
  imageUrl?: string | null;
  quantity?: number;
  /** e.g. "2 of 8 variants" */
  subtitle?: string | null;
  /**
   * True when the product (or every selected variant) is unavailable for sale.
   * Shows a small Out of Stock warning — no "Add stock" action.
   */
  outOfStock?: boolean;
  /** Alias: available === false also triggers the OOS badge */
  available?: boolean;
}

interface SelectedProductRowProps {
  product: SelectedProductLike;
  /** Show editable quantity (default true for free gifts) */
  showQuantity?: boolean;
  quantityLabel?: string;
  onQuantityChange?: (quantity: number) => void;
  onRemove: () => void;
  /** Optional edit — re-open picker for this product */
  onEdit?: () => void;
}

function isOutOfStock(product: SelectedProductLike): boolean {
  if (product.outOfStock === true) return true;
  if (product.available === false) return true;
  return false;
}

/**
 * Selected product chip: thumbnail + title + optional “N of M variants” + qty + remove.
 * Matches Shopify resource picker summary style (one row per product, not per variant).
 * Out-of-stock gifts show a warning badge (all campaign types).
 */
export function SelectedProductRow({
  product,
  showQuantity = true,
  quantityLabel = 'Qty',
  onQuantityChange,
  onRemove,
  onEdit,
}: SelectedProductRowProps) {
  const qty = Math.max(1, Number(product.quantity ?? 1) || 1);
  const oos = isOutOfStock(product);

  return (
    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
      <InlineStack align="space-between" blockAlign="center" wrap gap="300">
        <InlineStack gap="300" blockAlign="center">
          <Thumbnail
            source={product.imageUrl || PLACEHOLDER}
            alt={product.title}
            size="small"
          />
          <div>
            <InlineStack gap="150" blockAlign="center" wrap>
              <Text as="span" fontWeight="semibold">
                {product.title}
              </Text>
              {oos ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  title={t("This product is out of stock")}
                >
                  <span style={{ display: 'inline-flex', color: '#B98900' }}>
                    <Icon source={AlertCircleIcon} tone="warning" />
                  </span>
                  <Badge tone="warning">{t("Out of Stock")}</Badge>
                </span>
              ) : null}
            </InlineStack>
            {product.subtitle ? (
              <div style={{ marginTop: 2 }}>
                <Text as="span" tone="subdued" variant="bodySm">
                  {product.subtitle}
                </Text>
              </div>
            ) : null}
          </div>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          {showQuantity && onQuantityChange && (
            <div style={{ width: 72 }}>
              <TextField
                label={quantityLabel}
                labelHidden
                type="number"
                min={1}
                value={String(qty)}
                onChange={(v) => {
                  const n = Math.max(1, parseInt(v, 10) || 1);
                  onQuantityChange(n);
                }}
                autoComplete="off"
              />
            </div>
          )}
          {onEdit && (
            <Button variant="plain" onClick={onEdit} accessibilityLabel={t("Edit")}>
              {t("Edit")}
            </Button>
          )}
          <Button variant="plain" tone="critical" onClick={onRemove}>
            {t("Remove")}
          </Button>
        </InlineStack>
      </InlineStack>
    </Box>
  );
}
